import React from 'react';
import { range } from 'lodash-es';

import { Box, FormField, FormHelp, Input, Label, Text, Textarea, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../models/index';
import { Field, PaymentMethodSection } from '../../../models/index';
import { PAYMENT_METHOD } from '../../../constants';
import PaymentMethodListBox from './PaymentMethod/PaymentMethodListBox';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_PAYMENT_METHODS = 5;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PaymentMethodSection) => void;
};

export function PaymentMethod_v2({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: PaymentMethodSection,
  });

  const errorMsg = section.hasFieldError(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)
    ? PAYMENT_METHOD.ERROR_MESSAGE.ADDRESS
    : null;

  function onRemoveAll(): void {
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function onAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS]: value,
    });
  }

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(
      PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME,
      Number(index),
    );

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderIndividualPaymentMethod(index: number) {
    const nameField = section.getField(
      Field.getIndexedFieldName(PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME, index),
    );

    return (
      <FormField
        required
        css={{ marginTop: 'layout.tight' }}
        label={
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Label id={`electronic-payment-method-#${index + 1}`}>Service #{index + 1}</Label>
            {index > 0 && (
              <TextButton
                data-index={index}
                data-test="delete-electronic-payment-method"
                onClick={onDeleteOne}
                textStyle="body-sm-bold"
              >
                Delete
              </TextButton>
            )}
          </Box>
        }
        control={
          <Input
            aria-labelledby={`electronic-payment-method-#${index + 1}`}
            data-index={index}
            data-test="electronic-payment-method-name"
            value={nameField.enteredValue}
            onChange={setName}
          />
        }
      />
    );
  }

  const totalPaymentMethods = section.getTotalIndexes();
  if (totalPaymentMethods == null) {
    return null;
  }

  return (
    <>
      <PaymentMethodListBox
        section={section}
        setSectionValues={setSectionValues}
        onAddOne={onAddOne}
        onRemoveAll={onRemoveAll}
      />
      {section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED) && (
        <FormField
          required
          css={{ marginTop: 'layout.tight' }}
          label={<Label>To whom should the tenant send check or money order?</Label>}
          control={
            <Textarea
              data-test="tenant-send-check"
              error={!!errorMsg}
              onChange={onAddressChange}
              value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
              css={{ resize: 'vertical' }}
              rows={3}
            />
          }
          description={<FormHelp>Recipient’s name, street, city, state, and ZIP code</FormHelp>}
        />
      )}
      {range(0, totalPaymentMethods).map((index: number) => (
        <React.Fragment key={index}>{renderIndividualPaymentMethod(index)}</React.Fragment>
      ))}
      {totalPaymentMethods > 0 && (
        <Box css={{ marginTop: 'layout.tight' }}>
          {totalPaymentMethods < MAXIMUM_PAYMENT_METHODS ? (
            totalPaymentMethods >= 0 && (
              <TextButton icon={<IconPlusFilled />} onClick={onAddOne} data-test="add-another">
                Add another electronic payment service
              </TextButton>
            )
          ) : (
            <Text data-test="maximum-reached">
              You’ve added {MAXIMUM_PAYMENT_METHODS} electronic payment services, which is the maximum allowed.
            </Text>
          )}
        </Box>
      )}
    </>
  );
}

export default PaymentMethod_v2;
